import { useEffect, useMemo, useState, useCallback } from "react";
import { Users, Mail, Trash2, Loader2, Plus, Shield, Eye, Edit3, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABELS = {
  admin: { label: "Admin", icon: Shield, desc: "Full control, billing & invites" },
  member: { label: "Member", icon: Edit3, desc: "Create & edit resolutions" },
  viewer: { label: "Viewer", icon: Eye, desc: "Read-only access" },
};

export default function TeamSettings() {
  const { user, firm, role } = useAuth();
  const isAdmin = role === "admin";
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  // Pending invitations are filtered in two render spots — memoize so we
  // don't recreate the filtered list per render.
  const pendingInvitations = useMemo(
    () => invitations.filter((i) => !i.accepted_at),
    [invitations],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mr, ir] = await Promise.all([
        api.get("/firms/members"),
        api.get("/firms/invitations"),
      ]);
      setMembers(mr.data || []);
      setInvitations(ir.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post("/firms/invitations", { email: inviteEmail.trim(), role: inviteRole });
      toast.success(`Invited ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("member");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberUserId, newRole) => {
    try {
      await api.patch(`/firms/members/${memberUserId}`, { user_id: memberUserId, role: newRole });
      toast.success("Role updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    }
  };

  const handleRemove = async (memberUserId, email) => {
    if (!window.confirm(`Remove ${email} from ${firm?.name}?`)) return;
    try {
      await api.delete(`/firms/members/${memberUserId}`);
      toast.success("Member removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove member");
    }
  };

  const handleRevoke = async (invId, email) => {
    if (!window.confirm(`Revoke invitation for ${email}?`)) return;
    try {
      await api.delete(`/firms/invitations/${invId}`);
      toast.success("Invitation revoked");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke invitation");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8" data-testid="team-page">
      <div className="mb-8">
        <div className="label-eyebrow text-[11px] tracking-[0.2em] text-zinc-500 mb-1">SETTINGS</div>
        <h1 className="font-heading text-3xl tracking-tight font-light">Team</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Manage who has access to <span className="font-medium">{firm?.name || "this firm"}</span>.
        </p>
      </div>

      {/* Invite form (Admin only) */}
      {isAdmin && (
        <section className="border border-zinc-200 bg-white p-6 mb-8" data-testid="invite-form">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={14} className="text-[#002FA7]" />
            <span className="label-eyebrow">Invite by email</span>
          </div>
          <form onSubmit={handleInvite} className="flex flex-col md:flex-row gap-3">
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@yourfirm.com"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="invite-email-input"
              disabled={inviting}
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="border border-zinc-300 px-3 py-2 text-sm bg-white"
              data-testid="invite-role-select"
              disabled={inviting}
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="bg-[#002FA7] text-white px-5 py-2 text-sm font-medium hover:bg-[#001F70] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="invite-submit-button"
            >
              {inviting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Send invite
            </button>
          </form>
          <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
            Invited users join the firm with the chosen role the first time they sign in with Google.
            If the email already has a ROC Vault account, they're added instantly.
          </p>
        </section>
      )}

      {/* Members */}
      <section className="border border-zinc-200 bg-white mb-8" data-testid="members-section">
        <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
          <Users size={14} className="text-[#002FA7]" />
          <span className="label-eyebrow">Members ({members.length})</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {members.map((m) => {
              const RoleIcon = (ROLE_LABELS[m.role] || ROLE_LABELS.viewer).icon;
              return (
                <div key={m.user_id} className="px-6 py-4 flex items-center gap-4" data-testid={`member-row-${m.user_id}`}>
                  {m.picture ? (
                    <img src={m.picture} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-sm font-medium">
                      {(m.name || m.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate flex items-center gap-2">
                      {m.name || m.email}
                      {m.is_owner && (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-wider text-amber-700 border border-amber-300 px-1.5 py-0.5">
                          <Crown size={10} /> OWNER
                        </span>
                      )}
                      {m.is_self && (
                        <span className="text-[10px] tracking-wider text-zinc-500 border border-zinc-300 px-1.5 py-0.5">
                          YOU
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {isAdmin && !m.is_owner ? (
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                        className="border border-zinc-300 px-2 py-1 text-xs bg-white"
                        data-testid={`role-select-${m.user_id}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-700">
                        <RoleIcon size={12} />
                        {(ROLE_LABELS[m.role] || ROLE_LABELS.viewer).label}
                      </span>
                    )}
                    {isAdmin && !m.is_owner && !m.is_self && (
                      <button
                        onClick={() => handleRemove(m.user_id, m.email)}
                        className="text-zinc-400 hover:text-red-600"
                        data-testid={`remove-member-${m.user_id}`}
                        aria-label="Remove member"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Pending invitations */}
      {(isAdmin || invitations.some((i) => !i.accepted_at)) && (
        <section className="border border-zinc-200 bg-white" data-testid="invitations-section">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
            <Mail size={14} className="text-[#002FA7]" />
            <span className="label-eyebrow">
              Pending invitations ({invitations.filter((i) => !i.accepted_at).length})
            </span>
          </div>
          {pendingInvitations.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500 text-center">No pending invitations.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {pendingInvitations
                .map((inv) => (
                  <div key={inv.id} className="px-6 py-3 flex items-center gap-4" data-testid={`invitation-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900 truncate">{inv.email}</div>
                      <div className="text-[11px] text-zinc-500">
                        Invited as <span className="font-medium">{inv.role}</span>
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleRevoke(inv.id, inv.email)}
                        className="text-zinc-400 hover:text-red-600"
                        data-testid={`revoke-invitation-${inv.id}`}
                        aria-label="Revoke invitation"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
